// Flash — product page: variant picker, quantity tiers, design upload + skin preview.
// Loaded only on product pages. One instance per [data-product] section.
(() => {
  const SIZE_RE = /(\d+(?:[.,]\d+)?)\s*[x×*]\s*(\d+(?:[.,]\d+)?)/i;

  function formatMoney(cents, format) {
    const value = (cents / 100);
    const fmt = (n, dec, thou, decSep) => {
      const [i, d] = n.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (d ? decSep + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: fmt(value, 2, ',', '.'),
      amount_no_decimals: fmt(value, 0, ',', '.'),
      amount_with_comma_separator: fmt(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(value, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(value, 2, "'", '.'),
      amount_with_space_separator: fmt(value, 2, ' ', ','),
    }[key] ?? fmt(value, 2, ',', '.')));
  }

  function sizeOf(variant) {
    if (!variant) return null;
    for (const s of [...variant.options, variant.title]) {
      const m = String(s).match(SIZE_RE);
      if (m) return { w: parseFloat(m[1].replace(',', '.')), h: parseFloat(m[2].replace(',', '.')) };
    }
    return null;
  }

  function init(root) {
    const data = JSON.parse(root.querySelector('[data-product-json]').textContent);
    const { variants, moneyFormat, strings } = data;
    const form = document.getElementById(root.dataset.form);
    const idInput = form.querySelector('input[name="id"]');
    const priceEl = root.querySelector('[data-price]');
    const buyBtn = root.querySelector('[data-buy]');
    const qtyInput = root.querySelector('[data-qty-input]');
    let current = variants.find((v) => String(v.id) === idInput.value) || variants[0];

    const money = (c) => formatMoney(c, moneyFormat);
    const emit = () => root.dispatchEvent(new CustomEvent('flash:variant', { detail: { variant: current, size: sizeOf(current) } }));

    // ---- Variant picker
    const picker = root.querySelector('[data-picker]');
    const selected = () => [...picker.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);

    function paintAvailability() {
      const sel = selected();
      picker.querySelectorAll('fieldset').forEach((fs, i) => {
        fs.querySelectorAll('input').forEach((input) => {
          const probe = [...sel]; probe[i] = input.value;
          const ok = variants.some((v) => v.available && v.options.every((o, n) => o === probe[n]));
          input.classList.toggle('is-unavailable', !ok);
        });
        const label = fs.querySelector('[data-opt-label]');
        if (label) label.textContent = sel[i] || '';
      });
    }

    function render(initial) {
      if (priceEl) {
        const sale = current && current.compare_at_price > current.price;
        priceEl.classList.toggle('price--sale', !!sale);
        priceEl.innerHTML = current
          ? (sale ? `<ins>${money(current.price)}</ins> <s>${money(current.compare_at_price)}</s>` : `<span>${money(current.price)}</span>`)
          : `<span>${strings.unavailable}</span>`;
      }
      if (buyBtn) {
        const ok = current && current.available;
        buyBtn.disabled = !ok;
        buyBtn.querySelector('span').textContent = !current ? strings.unavailable : ok ? strings.add : strings.soldOut;
      }
      if (current) {
        idInput.value = current.id;
        if (!initial) {
        const url = new URL(location.href);
        url.searchParams.set('variant', current.id);
        history.replaceState({}, '', url);
        }
        const media = current.featured_media && root.querySelector(`[data-media-id="${current.featured_media.id}"]`);
        const slider = root.querySelector('[data-slider]');
        if (media && slider && slider.scrollWidth > slider.clientWidth) slider.scrollTo({ left: media.offsetLeft - slider.offsetLeft, behavior: 'smooth' });
      }
      emit();
    }

    if (picker) {
      picker.addEventListener('change', () => {
        const sel = selected();
        current = variants.find((v) => v.options.every((o, i) => o === sel[i]));
        paintAvailability();
        render();
      });
      paintAvailability();
    }

    // ---- Quantity tiers (informative: real discounts are Shopify automatic discounts)
    const tiersEl = root.querySelector('[data-tiers]');
    if (tiersEl && qtyInput) {
      const tiers = JSON.parse(tiersEl.dataset.tiers).sort((a, b) => a.min - b.min);
      const totalEl = root.querySelector('[data-total]');
      const update = () => {
        if (!current) return;
        const q = Math.max(1, Number(qtyInput.value) || 1);
        const tier = tiers.filter((t) => q >= t.min).pop();
        const pct = tier ? tier.pct : 0;
        tiersEl.querySelectorAll('tr').forEach((tr) => tr.classList.toggle('is-active', tier && Number(tr.dataset.min) === tier.min));
        tiersEl.querySelectorAll('[data-unit]').forEach((td) => {
          td.textContent = money(Math.round(current.price * (1 - Number(td.dataset.unit) / 100)));
        });
        if (totalEl) {
          const total = Math.round(current.price * q * (1 - pct / 100));
          totalEl.innerHTML = money(total) + (pct ? ` <small>(−${pct}%)</small>` : '');
        }
      };
      qtyInput.addEventListener('change', update);
      qtyInput.addEventListener('input', update);
      root.addEventListener('flash:variant', update);
      update();
    }

    // ---- Design upload + preview on skin
    const custom = root.querySelector('[data-custom]');
    if (custom) {
      const file = custom.querySelector('input[type=file]');
      const drop = custom.querySelector('.drop');
      const dropText = drop.querySelector('strong');
      const dropDefault = dropText.textContent;
      const mock = custom.querySelector('.mock');
      const art = custom.querySelector('.mock__art');
      const dim = custom.querySelector('.mock__dim');
      const msg = custom.querySelector('[data-msg]');
      const maxMb = Number(custom.dataset.maxMb || 20);
      const refCm = Number(custom.dataset.refCm || 14);
      let size = sizeOf(current);
      let objectUrl;

      const say = (text, kind) => { msg.textContent = text || ''; msg.className = 'custom__msg' + (kind ? ` notice notice--${kind}` : ''); };

      const fit = () => {
        if (!size || !mock.classList.contains('is-on')) return;
        dim.textContent = `${size.w} × ${size.h} cm`;
        // Scale the design to its real size relative to the forearm in the mock.
        const box = Math.min(1, size.w / refCm) * 100;
        const ratioArt = art.naturalWidth / art.naturalHeight;
        const ratioBox = size.w / size.h;
        art.style.width = ratioArt >= ratioBox ? `${box}%` : `${box * ratioArt / ratioBox}%`;
        art.style.maxHeight = '84%';
        // Resolution check: 300 dpi is ideal, warn under ~150 dpi.
        const needPx = (Math.max(size.w, size.h) / 2.54) * 150;
        const havePx = Math.max(art.naturalWidth, art.naturalHeight);
        if (havePx < needPx) say(strings.lowRes, 'error'); else say(strings.ok, 'ok');
      };

      const handle = () => {
        const f = file.files[0];
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        mock.classList.remove('is-on');
        drop.classList.remove('has-file');
        dropText.textContent = dropDefault;
        say('');
        if (!f) return;
        if (f.size > maxMb * 1024 * 1024) { say(strings.tooBig.replace('{{ size }}', maxMb), 'error'); file.value = ''; return; }
        if (!/^image\/|application\/pdf/.test(f.type)) { say(strings.badType, 'error'); file.value = ''; return; }
        drop.classList.add('has-file');
        dropText.textContent = f.name;
        if (f.type === 'application/pdf') { say(strings.ok, 'ok'); return; }
        objectUrl = URL.createObjectURL(f);
        art.onload = () => { mock.classList.add('is-on'); fit(); };
        art.src = objectUrl;
      };

      file.addEventListener('change', handle);
      ['dragenter', 'dragover'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, () => drop.classList.remove('is-over')));
      drop.addEventListener('drop', (e) => {
        e.preventDefault();
        if (!e.dataTransfer.files.length) return;
        file.files = e.dataTransfer.files;
        handle();
      });
      custom.querySelectorAll('[data-skin]').forEach((b) => b.addEventListener('click', () => {
        custom.querySelectorAll('[data-skin]').forEach((x) => x.setAttribute('aria-pressed', x === b));
        mock.style.setProperty('--skin', b.dataset.skin);
      }));
      root.addEventListener('flash:variant', (e) => { size = e.detail.size; fit(); });
    }

    // ---- Sticky add-to-cart (mobile): shown once the main button has scrolled away
    const sticky = root.querySelector('[data-sticky-buy]');
    if (sticky && buyBtn && 'IntersectionObserver' in window) {
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      const stickyBtn = sticky.querySelector('button');
      new IntersectionObserver(([e]) => {
        sticky.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0);
      }).observe(buyBtn);
      root.addEventListener('flash:variant', (e) => {
        const v = e.detail.variant;
        stickyPrice.textContent = v ? money(v.price) : strings.unavailable;
        stickyBtn.disabled = !(v && v.available);
      });
    }

    // ---- Submit feedback
    form.addEventListener('submit', () => { if (buyBtn) { buyBtn.classList.add('is-loading'); buyBtn.querySelector('span').textContent = strings.adding; } });
    window.addEventListener('pageshow', () => { if (buyBtn) buyBtn.classList.remove('is-loading'); render(true); });

    render(true);
  }

  document.querySelectorAll('[data-product]').forEach(init);
})();
